import React, { useCallback, useState, type FC } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import {
	FlyoutMenuItem,
	FlyoutMenuItemContent,
	FlyoutMenuItemTrigger,
} from '@atlaskit/navigation-system/side-nav-items/flyout-menu-item';

import { useNotesActions } from '@confluence/notes-util/entry-points/NotesStore.ts';
import { START_TOUCH } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import { useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useFlyout } from '@confluence/flyout-store';
import { LoadableLazy } from '@confluence/loadable';
import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener.ts';
import { UNIVERSAL_CREATE_NOTE_SHORTCUT } from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { useNotesData } from '@confluence/notes-util/entry-points/hooks/useNotesData.ts';
import { triggerCreationTrackEvent } from '@confluence/notes-util/entry-points/notesCreationAnalytics.ts';
import { NotesOnboardingSpotlight } from '@confluence/onboarding-notes/entry-points/NotesOnboardingSpotlight.tsx';
import {
	useGetOnboardingState,
	useSetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { NOTES_ONBOARDING_SPOTLIGHT } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { fg } from '@confluence/feature-gating';

import { NotesIconComponent } from '../GlobalNavigationIcons.tsx';
import { ContentMenuLoadingState } from '../../ContentMenu/ContentMenuStates.tsx';

import type { GlobalItemProps } from './globalItemProps.ts';
import { useGlobalItemVisibility } from './useGlobalItemVisibility.ts';

const NotesMenuLoader = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-NotesMenu" */
				'../../ContentMenu/NotesMenu.tsx'
			)
		).NotesMenu,
	loading: () => <ContentMenuLoadingState />,
});

const i18n = defineMessages({
	notes: {
		id: 'side-navigation.global.notes',
		defaultMessage: 'Notes',
		description: "Label text for a flyout item title that opens a menu of the user's notes.",
	},
});

const MENU_ID = 'notes';

export const NotesItem: FC<GlobalItemProps> = ({ isHidden, peekingId, setPeekingId }) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const routeDataRef = useRouteDataRef();
	const { isFlyoutOpen, openFlyout, closeFlyout } = useFlyout(MENU_ID);
	const { setActiveNoteId } = useNotesActions();
	const { setOnboardingState } = useSetOnboardingState();
	const [hasHovered, setHasHovered] = useState(false);

	const { data: onboardingStateData, loading: isOnboardingStateLoading } = useGetOnboardingState(
		NOTES_ONBOARDING_SPOTLIGHT.SPOTLIGHT_DISMISSED,
		!fg('cc_notes_onboarding_spotlight'),
	);

	const { hasSpotlightDismissed } = deserializeState(onboardingStateData);

	const onNoteClick = useCallback(
		(noteId: string) => {
			setActiveNoteId(noteId);
			closeFlyout();
		},
		[closeFlyout, setActiveNoteId],
	);

	const onClickNavItem = useCallback(() => {
		if (!isFlyoutOpen) {
			openFlyout();
		} else {
			closeFlyout();
		}

		if (!hasSpotlightDismissed) {
			setOnboardingState({
				key: NOTES_ONBOARDING_SPOTLIGHT.SPOTLIGHT_DISMISSED,
				value: 'true',
			});
		}

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'globalNavigation',
				action: 'clicked',
				actionSubject: 'navigationItem',
				actionSubjectId: 'notes',
				attributes: {
					selectedItemPageContext: routeDataRef.current?.routeName,
					navigationLayer: 'global',
					navVersion: '4',
					navdexPointType: START_TOUCH,
				},
			},
		}).fire();
	}, [
		createAnalyticsEvent,
		isFlyoutOpen,
		openFlyout,
		closeFlyout,
		hasSpotlightDismissed,
		setOnboardingState,
		routeDataRef,
	]);

	const onNoteCreate = useCallback(() => {
		if (isFlyoutOpen) {
			closeFlyout();
		}
	}, [isFlyoutOpen, closeFlyout]);

	const { createNote } = useNotesData();

	const handleNoteCreation = useCallback(async () => {
		// Currently, we are sending in an empty adf body for note creation, but may need to replace this
		// when the BE is capable of handling a null body

		const { data } = await createNote({
			variables: { body: '{"version":1,"type":"doc","content":[]}' },
		});

		setActiveNoteId(data?.createNote?.note?.ari);
		if (data?.createNote?.success && data?.createNote?.note?.ari) {
			triggerCreationTrackEvent({
				createAnalyticsEvent,
				noteId: data.createNote.note.ari,
				entryPoint: 'createNoteShortcut',
			});
		}
		onNoteCreate?.();
	}, [createNote, setActiveNoteId, onNoteCreate, createAnalyticsEvent]);

	const onOpenChange = useCallback(
		(newIsOpen: boolean) => {
			if (!newIsOpen) {
				isFlyoutOpen && closeFlyout();
			} else {
				openFlyout();
			}
		},
		[isFlyoutOpen, closeFlyout, openFlyout],
	);

	const handleMouseEnter = useCallback(() => {
		if (fg('cc_notes_onboarding_spotlight')) {
			setHasHovered(true);
		}
	}, []);

	const shouldHide = useGlobalItemVisibility(
		MENU_ID,
		isFlyoutOpen,
		isHidden,
		peekingId,
		setPeekingId,
	);

	if (shouldHide) {
		return null;
	}

	const notesMenuItem = (
		<FlyoutMenuItem onOpenChange={onOpenChange} isOpen={isFlyoutOpen}>
			<FlyoutMenuItemTrigger elemBefore={NotesIconComponent} onClick={onClickNavItem}>
				<FormattedMessage {...i18n.notes} />
			</FlyoutMenuItemTrigger>

			{/* autofocus disabled to keep focus on filter field when opened  */
			/*eslint-disable-next-line @atlassian/a11y/no-autofocus*/}
			<FlyoutMenuItemContent onClose={() => isFlyoutOpen && closeFlyout()} autoFocus={false}>
				<NotesMenuLoader onNoteClick={onNoteClick} onNoteCreate={onNoteCreate} />
			</FlyoutMenuItemContent>
			<GeneralShortcutListener
				accelerator={UNIVERSAL_CREATE_NOTE_SHORTCUT}
				listener={handleNoteCreation}
				isAppNavigationShortcut
			/>
		</FlyoutMenuItem>
	);

	const shouldShowSpotlight =
		!isOnboardingStateLoading && !!onboardingStateData && !hasSpotlightDismissed && hasHovered;

	return (
		<div
			onMouseEnter={handleMouseEnter}
			onFocus={handleMouseEnter}
			data-testid="notes-item-wrapper"
			role="button"
			tabIndex={0}
		>
			<NotesOnboardingSpotlight
				shouldShow={shouldShowSpotlight && fg('cc_notes_onboarding_spotlight')}
			>
				{notesMenuItem}
			</NotesOnboardingSpotlight>
		</div>
	);
};
